import { isFunction } from "lodash";
import { ChangeEvent, forwardRef, useEffect, useMemo, useState } from "react";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";

export type Props = {
  value?: boolean;
  label?: string;
  text: string;
  defaultChecked: boolean;
  required?: boolean;
  onChange?: (value: boolean) => void;
  name?: string;
  isErrored?: boolean;
};

export const Checkbox = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      value,
      label: labelText,
      text,
      defaultChecked,
      required,
      onChange,
      isErrored,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);

    useEffect(() => {
      if (value === undefined && isFunction(onChange)) onChange(false);
    }, [value, onChange]);

    const handleChange = useMemo(() => {
      return onChange !== undefined
        ? (e: ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.checked);
          }
        : undefined;
    }, [onChange]);

    return (
      <div className="-ml-0.5 flex min-h-fit flex-nowrap items-center gap-x-2.5 overflow-hidden rounded-lg text-[17px] text-zi-text">
        <label
          data-errored={isErrored}
          className="flex cursor-pointer items-center gap-1 text-base text-zi-text data-[errored=true]:text-zi-formError"
        >
          <input
            className="peer absolute h-px w-px overflow-hidden whitespace-nowrap [clip-path:inset(50%)] [clip:rect(0_0_0_0)]"
            ref={ref}
            type="checkbox"
            name={name}
            checked={value ?? defaultChecked}
            onChange={handleChange}
            {...props}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          <span
            data-errored={isErrored}
            data-focused={focused}
            className="text-zi-gray peer-checked:text-zi-primary data-[errored=true]:text-zi-formError data-[focused=true]:text-zi-primary"
          >
            {value ?? defaultChecked ? (
              <MdCheckBox size={28} />
            ) : (
              <MdCheckBoxOutlineBlank size={28} />
            )}
          </span>
          {text}{" "}
          {required && !labelText && (
            <span className="ml-0.5 font-normal text-zi-secondaryText">
              (required)
            </span>
          )}
        </label>
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";
